import { memo, useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { X, TrendingUp, Users, Building, Heart } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { useAuth } from "@/contexts/AuthContext";
import { useTeamId } from "@/hooks/useTeamId";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { getPillarScores } from "@/services/scoring/scoringQueries";

interface MySuccessionScoreOverlayProps {
  onClose: () => void;
}

const pillarMeta: Record<string, { icon: React.ReactNode; description: string }> = {
  "Transition Readiness": {
    icon: <Heart className="h-5 w-5" />,
    description: "Preparation mentally, emotionally, and financially for the exit and transition of the business, ensuring business owner's vision for the future aligns with the business's succession plan.",
  },
  "Strategic Planning": {
    icon: <TrendingUp className="h-5 w-5" />,
    description: "Development of a long-term business strategy that includes planning for the business's future direction and how it will be transitioned during the succession.",
  },
  "Business Performance": {
    icon: <Users className="h-5 w-5" />,
    description: "Ensuring the business is financially healthy, profitable, and attractive to potential buyers or successors, with a focus on improving performance and business valuation.",
  },
  "Governance and Compliance": {
    icon: <Users className="h-5 w-5" />,
    description: "Establishing strong governance structures, legal frameworks, and ensuring enhanced compliance to facilitate a smooth ownership transfer and minimize transition risk.",
  },
  "Leadership and Development": {
    icon: <Building className="h-5 w-5" />,
    description: "Cultivating and developing future leaders to support the business to ensure that the organization and its clients have a capable leadership team ready for the post-transition.",
  },
};

function getScoreColor(score: number) {
  if (score >= 70) return "text-teal-600";
  if (score >= 50) return "text-amber-600";
  return "text-red-600";
}

function getProgressColor(score: number) {
  if (score >= 70) return "[&>div]:bg-teal-500";
  if (score >= 50) return "[&>div]:bg-amber-500";
  return "[&>div]:bg-red-500";
}

export const MySuccessionScoreOverlay = memo(function MySuccessionScoreOverlay({
  onClose
}: MySuccessionScoreOverlayProps) {
  const { user } = useAuth();
  const { effectiveTeamId, loading: teamLoading } = useTeamId();
  const [datapointCount, setDatapointCount] = useState<number>(0);

  useEffect(() => {
    async function loadDatapointCount() {
      if (!user?.id) return;
      try {
        const { count } = await supabase
          .from('user_mcf_values')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id);
        setDatapointCount(count ?? 0);
      } catch (err) {
        console.error('Failed to load datapoint count:', err);
      }
    }
    loadDatapointCount();
  }, [user?.id]);

  const { data: pillarData, isLoading: pillarsLoading } = useQuery({
    queryKey: ["overlayPillarScores", user?.id, effectiveTeamId],
    queryFn: () => getPillarScores(user!.id, effectiveTeamId!),
    staleTime: 30_000,
    enabled: !!user?.id && !!effectiveTeamId && !teamLoading,
  });

  const fallbackPillars = [
    "Transition Readiness",
    "Strategic Planning",
    "Business Performance",
    "Governance and Compliance",
    "Leadership and Development",
  ];

  const pillarCards = (pillarData && pillarData.length > 0)
    ? pillarData.map(p => ({
        title: p.pillarName,
        value: p.score,
        loaded: true,
      }))
    : fallbackPillars.map(title => ({
        title,
        value: null as number | null,
        loaded: !pillarsLoading,
      }));

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-background rounded-xl shadow-lg max-w-4xl w-full h-[80vh] overflow-y-auto p-8 relative">
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="absolute top-4 right-4 h-8 w-8 hover:text-foreground"
        >
          <X className="h-5 w-5" />
        </Button>
        <div className="pr-8 mb-6">
          <h1 className="text-2xl font-semibold">About mySuccession Score</h1>
          <p className="text-muted-foreground">
            Your mySuccession score evaluates {datapointCount} datapoints across the following five key dimensions:
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {pillarCards.map((card) => {
            const meta = pillarMeta[card.title] ?? {
              icon: <TrendingUp className="h-5 w-5" />,
              description: "",
            };
            const isLoading = !card.loaded;
            const hasScore = card.value != null;
            const colorClass = hasScore ? getScoreColor(card.value!) : "text-muted-foreground";
            const progressColorClass = hasScore ? getProgressColor(card.value!) : "";

            return (
              <Card key={card.title} className="relative overflow-hidden h-fit">
                <CardHeader className="pb-3 p-4">
                  <div className="flex items-center gap-2 min-h-[24px]">
                    <div className={hasScore ? colorClass : "text-muted-foreground"}>
                      {meta.icon}
                    </div>
                    <CardTitle className="text-base leading-tight">{card.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3 p-4 pt-0">
                  <div className="flex items-baseline gap-2 min-h-[40px]">
                    {isLoading ? (
                      <div className="h-7 w-16 rounded bg-muted animate-pulse" />
                    ) : hasScore ? (
                      <div className={`text-2xl font-bold ${colorClass}`}>
                        {card.value!.toFixed(1)}%
                      </div>
                    ) : (
                      <div className="text-2xl font-bold text-muted-foreground">—</div>
                    )}
                  </div>
                  {isLoading ? (
                    <div className="h-2 w-full rounded-full bg-muted animate-pulse" />
                  ) : (
                    <Progress
                      value={hasScore ? Math.min(card.value!, 100) : 0}
                      className={`h-2 ${progressColorClass}`}
                    />
                  )}
                  <CardDescription className="text-xs leading-relaxed min-h-[60px]">
                    {meta.description}
                  </CardDescription>
                </CardContent>
              </Card>
            );
          })}
          
          <Card className="relative overflow-hidden metric-card-primary h-fit">
            <CardHeader className="pb-3 p-4">
              <div className="flex items-center gap-2 min-h-[24px]">
                <div className="text-semantic-primary">
                  <TrendingUp className="h-5 w-5" />
                </div>
                <CardTitle className="text-base leading-tight">Overall Assessment</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3 p-4 pt-0">
              <div className="min-h-[40px] my-0"></div>
              <CardDescription className="text-xs leading-relaxed min-h-[60px]">
                Your mySuccession Score of 68% indicates strong foundation with room for improvement in people connections and organizational alignment. Focus on enhancing team relationships and clarifying organizational values to maximize succession readiness.
              </CardDescription>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
});

MySuccessionScoreOverlay.displayName = "MySuccessionScoreOverlay";

export type PillarStatus = 'implemented' | 'planned' | 'future';
